import { Checkout } from '../../../../../checkout/react/models/Checkout';
import { IExtensionGroup } from '../../../../../extension-groups/models/extension-group.models';
import { AbTest } from '../../../models/AbTest';
import { IAbTestActions } from '../../../models/AbTestActions';
import { ICheckoutExtensions } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { Theme, ProductPage } from './components/TestVariantTable/TestVariantTable';
import { SmartCart } from '../../ab-test-modal/hooks/useSmartCarts';
import { CheckoutProfile } from '../../ab-test-modal/hooks/useCheckoutProfiles';

export interface AbTestCardProps {
  abTest: AbTest;
  checkouts: Checkout[];
  extensions: ICheckoutExtensions[];
  extensionGroups: IExtensionGroup[];
  runningAbTestCheckoutIds: number[];
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  abTestActions: IAbTestActions;
  freeShippingThreshold?: number | null;
  bigSpenderThreshold?: number | null;
  themes?: Theme[];
  productPages?: ProductPage[];
  smartCarts?: SmartCart[];
  checkoutProfiles?: CheckoutProfile[];
}
export interface SegmentMetric {
  // Checkout/Shipping test fields
  TOTAL_CHECKOUTS: number;
  STATISTICAL_SIGNIFICANCE_ARPC: number;
  UPLIFT_CR_ABS: number;
  UPLIFT_ARPC_ABS: number;
  // Cross-Site test fields
  TOTAL_USERS?: number;
  STATISTICAL_SIGNIFICANCE_UCVR?: number;
  UPLIFT_UCVR_ABS?: number;
  UPLIFT_ARPU_ABS?: number;
  // Shared fields
  PERCENT_TRAFFIC: number;
  UPLIFT_AOV_ABS: number;
  ANNUALIZE_UPLIFT_CVR: number;
  SEGMENT: string;
  /** QuickSight segment filter label, e.g. "(03) First Time customers " */
  SEGMENT_MAPPING_CLEAN?: string | null;

  // Checkout/Shipping test fields, no outliers fields
  TOTAL_CHECKOUTS_NO_OUTLIERS: number;
  STATISTICAL_SIGNIFICANCE_ARPC_NO_OUTLIERS: number;
  UPLIFT_CR_ABS_NO_OUTLIERS: number;
  UPLIFT_ARPC_ABS_NO_OUTLIERS: number;

  // Cross-Site test fields
  TOTAL_USERS_NO_OUTLIERS?: number;
  STATISTICAL_SIGNIFICANCE_UCVR_NO_OUTLIERS?: number;
  UPLIFT_UCVR_ABS_NO_OUTLIERS?: number;
  UPLIFT_ARPU_ABS_NO_OUTLIERS?: number;

  // Shared fields, no outliers fields
  PERCENT_TRAFFIC_NO_OUTLIERS: number;
  UPLIFT_AOV_ABS_NO_OUTLIERS: number;
  ANNUALIZE_UPLIFT_CVR_NO_OUTLIERS: number;
}

export interface SegmentFieldMapping {
  totalCheckouts: keyof SegmentMetric;
  totalUsers: keyof SegmentMetric;
  upliftCr: keyof SegmentMetric;
  upliftUcvr: keyof SegmentMetric;
  upliftArpc: keyof SegmentMetric;
  upliftArpu: keyof SegmentMetric;
  upliftAov: keyof SegmentMetric;
  annualizedLift: keyof SegmentMetric;
  statSigArpc: keyof SegmentMetric;
  statSigUcvr: keyof SegmentMetric;
  percentTraffic: keyof SegmentMetric;
}

export const DEFAULT_FIELDS: SegmentFieldMapping = {
  totalCheckouts: 'TOTAL_CHECKOUTS',
  totalUsers: 'TOTAL_USERS',
  upliftCr: 'UPLIFT_CR_ABS',
  upliftUcvr: 'UPLIFT_UCVR_ABS',
  upliftArpc: 'UPLIFT_ARPC_ABS',
  upliftArpu: 'UPLIFT_ARPU_ABS',
  upliftAov: 'UPLIFT_AOV_ABS',
  annualizedLift: 'ANNUALIZE_UPLIFT_CVR',
  statSigArpc: 'STATISTICAL_SIGNIFICANCE_ARPC',
  statSigUcvr: 'STATISTICAL_SIGNIFICANCE_UCVR',
  percentTraffic: 'PERCENT_TRAFFIC',
};

export const NO_OUTLIERS_FIELDS: SegmentFieldMapping = {
  totalCheckouts: 'TOTAL_CHECKOUTS_NO_OUTLIERS',
  totalUsers: 'TOTAL_USERS_NO_OUTLIERS',
  upliftCr: 'UPLIFT_CR_ABS_NO_OUTLIERS',
  upliftUcvr: 'UPLIFT_UCVR_ABS_NO_OUTLIERS',
  upliftArpc: 'UPLIFT_ARPC_ABS_NO_OUTLIERS',
  upliftArpu: 'UPLIFT_ARPU_ABS_NO_OUTLIERS',
  upliftAov: 'UPLIFT_AOV_ABS_NO_OUTLIERS',
  annualizedLift: 'ANNUALIZE_UPLIFT_CVR_NO_OUTLIERS',
  statSigArpc: 'STATISTICAL_SIGNIFICANCE_ARPC_NO_OUTLIERS',
  statSigUcvr: 'STATISTICAL_SIGNIFICANCE_UCVR_NO_OUTLIERS',
  percentTraffic: 'PERCENT_TRAFFIC_NO_OUTLIERS',
};

export interface TestVariantMetric {
  TOTAL_CHECKOUTS: number;
  UPLIFT_ARPC_ABS: number;
  SEGMENT: string;
}
